import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { masterDataApi } from '../../services/api';
import { handleApiError, showSuccess } from '../../utils/handleApiError';
import ConfirmModal from '../ConfirmModal';

interface MasterDataCategory {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  values?: MasterDataValue[];
}

interface MasterDataValue {
  id: number;
  category_id: number;
  key: string;
  value: string;
  display_value?: string;
  daily_weight_gain?: number;
  sort_order: number;
  is_active: boolean;
}

const MasterDataManagement: React.FC = () => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState<MasterDataCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<MasterDataCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showValueForm, setShowValueForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MasterDataCategory | null>(null);
  const [editingValue, setEditingValue] = useState<MasterDataValue | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ action: 'deleteCategory' | 'deleteValue'; id: number } | null>(null);

  const [newCategory, setNewCategory] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true
  });

  const [newValue, setNewValue] = useState<{
    key: string;
    value: string;
    display_value: string;
    daily_weight_gain?: number;
    sort_order: number;
    is_active: boolean;
  }>({
    key: '',
    value: '',
    display_value: '',
    daily_weight_gain: undefined,
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await masterDataApi.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      handleApiError(error, 'Hiba történt a kategóriák betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await masterDataApi.createCategory(newCategory);
      showSuccess('Kategória sikeresen létrehozva');
      setShowCategoryForm(false);
      setNewCategory({ name: '', display_name: '', description: '', is_active: true });
      loadCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      handleApiError(error, 'Hiba történt a kategória létrehozásakor');
    }
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      await masterDataApi.updateCategory(editingCategory.id, editingCategory);
      showSuccess('Kategória sikeresen frissítve');
      setEditingCategory(null);
      setShowCategoryForm(false);
      loadCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      handleApiError(error, 'Hiba történt a kategória frissítésekor');
    }
  };

  const handleCreateValue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) return;

    try {
      await masterDataApi.createValue(selectedCategory.id, newValue);
      showSuccess('Érték sikeresen létrehozva');
      setShowValueForm(false);
      setNewValue({ key: '', value: '', display_value: '', daily_weight_gain: undefined, sort_order: 0, is_active: true });
      loadCategoryValues(selectedCategory.id);
    } catch (error) {
      console.error('Error creating value:', error);
      handleApiError(error, 'Hiba történt az érték létrehozásakor');
    }
  };

  const handleUpdateValue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingValue) return;

    try {
      await masterDataApi.updateValue(editingValue.id, editingValue);
      showSuccess('Érték sikeresen frissítve');
      setEditingValue(null);
      setShowValueForm(false);
      if (selectedCategory) {
        loadCategoryValues(selectedCategory.id);
      }
    } catch (error) {
      console.error('Error updating value:', error);
      handleApiError(error, 'Hiba történt az érték frissítésekor');
    }
  };

  const loadCategoryValues = async (categoryId: number) => {
    try {
      const data = await masterDataApi.getCategoryValues(categoryId);
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        category.values = data;
        setSelectedCategory({ ...category, values: data });
      }
    } catch (error) {
      console.error('Error loading category values:', error);
      handleApiError(error, 'Hiba történt a kategória értékeinek betöltésekor');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { action, id } = confirmAction;

    try {
      if (action === 'deleteCategory') {
        await masterDataApi.deleteCategory(id);
        showSuccess('Kategória sikeresen törölve');
        loadCategories();
        if (selectedCategory?.id === id) {
          setSelectedCategory(null);
        }
      } else if (action === 'deleteValue') {
        await masterDataApi.deleteValue(id);
        showSuccess('Érték sikeresen törölve');
        if (selectedCategory) {
          loadCategoryValues(selectedCategory.id);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      handleApiError(error, action === 'deleteCategory'
        ? 'Hiba történt a kategória törlésekor'
        : 'Hiba történt az érték törlésekor');
    } finally {
      setConfirmAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Categories List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t('categories', 'admin')}
            </h3>
            <button
              onClick={() => {
                setEditingCategory(null);
                setNewCategory({ name: '', display_name: '', description: '', is_active: true });
                setShowCategoryForm(true);
              }}
              className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700"
            >
              + {t('add_category', 'admin')}
            </button>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                selectedCategory?.id === category.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''
              }`}
              onClick={() => {
                setSelectedCategory(category);
                loadCategoryValues(category.id);
              }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900">{category.display_name}</h4>
                  <p className="text-sm text-gray-500">{category.name}</p>
                  {category.description && (
                    <p className="text-xs text-gray-400 mt-1">{category.description}</p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCategory(category);
                      setShowCategoryForm(true);
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmAction({ action: 'deleteCategory', id: category.id });
                    }}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Values List */}
      <div className="lg:col-span-2 bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {selectedCategory ? `${selectedCategory.display_name} - ${t('values', 'admin')}` : t('values', 'admin')}
            </h3>
            {selectedCategory && (
              <button
                onClick={() => {
                  setEditingValue(null);
                  setNewValue({ key: '', value: '', display_value: '', daily_weight_gain: undefined, sort_order: 0, is_active: true });
                  setShowValueForm(true);
                }}
                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700"
              >
                + {t('add_value', 'admin')}
              </button>
            )}
          </div>
        </div>
        
        {selectedCategory ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('key', 'admin')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('value', 'admin')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('display_name', 'admin')}
                  </th>
                  {selectedCategory.name === 'animal_breeds' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Napi hízás (kg/nap)
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sort_order', 'admin')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('is_active', 'admin')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions', 'action')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedCategory.values?.map((value) => (
                  <tr key={value.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {value.key}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {value.value}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {value.display_value || '-'}
                    </td>
                    {selectedCategory.name === 'animal_breeds' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {value.daily_weight_gain ? `${value.daily_weight_gain} kg/nap` : '-'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {value.sort_order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        value.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {value.is_active ? t('yes', 'general') : t('no', 'general')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingValue(value);
                            setShowValueForm(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {t('edit', 'action')}
                        </button>
                        <button
                          onClick={() => setConfirmAction({ action: 'deleteValue', id: value.id })}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('delete', 'action')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5m14 14H5" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Válassz egy kategóriát</h3>
            <p className="mt-1 text-sm text-gray-500">Az értékek megjelenítéséhez válassz egy kategóriát a bal oldalon.</p>
          </div>
        )}
      </div>

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingCategory ? t('edit_category', 'admin') : t('add_category', 'admin')}
            </h3>
            <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('category_name', 'admin')} *</label>
                <input
                  type="text"
                  required
                  value={editingCategory ? editingCategory.name : newCategory.name}
                  onChange={(e) => editingCategory 
                    ? setEditingCategory({ ...editingCategory, name: e.target.value })
                    : setNewCategory({ ...newCategory, name: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('display_name', 'admin')} *</label>
                <input
                  type="text"
                  required
                  value={editingCategory ? editingCategory.display_name : newCategory.display_name}
                  onChange={(e) => editingCategory 
                    ? setEditingCategory({ ...editingCategory, display_name: e.target.value })
                    : setNewCategory({ ...newCategory, display_name: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={editingCategory ? editingCategory.description || '' : newCategory.description}
                  onChange={(e) => editingCategory 
                    ? setEditingCategory({ ...editingCategory, description: e.target.value })
                    : setNewCategory({ ...newCategory, description: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  rows={3}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingCategory ? editingCategory.is_active : newCategory.is_active}
                  onChange={(e) => editingCategory 
                    ? setEditingCategory({ ...editingCategory, is_active: e.target.checked })
                    : setNewCategory({ ...newCategory, is_active: e.target.checked })
                  }
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">{t('is_active', 'admin')}</label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryForm(false);
                    setEditingCategory(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Value Form Modal */}
      {showValueForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingValue ? t('edit_value', 'admin') : t('add_value', 'admin')}
            </h3>
            <form onSubmit={editingValue ? handleUpdateValue : handleCreateValue} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('key', 'admin')} *</label>
                <input
                  type="text"
                  required
                  value={editingValue ? editingValue.key : newValue.key}
                  onChange={(e) => editingValue 
                    ? setEditingValue({ ...editingValue, key: e.target.value })
                    : setNewValue({ ...newValue, key: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('value', 'admin')} *</label>
                <input
                  type="text"
                  required
                  value={editingValue ? editingValue.value : newValue.value}
                  onChange={(e) => editingValue 
                    ? setEditingValue({ ...editingValue, value: e.target.value })
                    : setNewValue({ ...newValue, value: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('display_name', 'admin')}</label>
                <input
                  type="text"
                  value={editingValue ? editingValue.display_value || '' : newValue.display_value}
                  onChange={(e) => editingValue 
                    ? setEditingValue({ ...editingValue, display_value: e.target.value })
                    : setNewValue({ ...newValue, display_value: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('sort_order', 'admin')}</label>
                <input
                  type="number"
                  value={editingValue ? editingValue.sort_order : newValue.sort_order}
                  onChange={(e) => editingValue
                    ? setEditingValue({ ...editingValue, sort_order: parseInt(e.target.value) || 0 })
                    : setNewValue({ ...newValue, sort_order: parseInt(e.target.value) || 0 })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
              {selectedCategory?.name === 'animal_breeds' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Napi hízás (kg/nap)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={editingValue ? editingValue.daily_weight_gain || '' : newValue.daily_weight_gain || ''}
                    onChange={(e) => editingValue
                      ? setEditingValue({ ...editingValue, daily_weight_gain: parseFloat(e.target.value) || undefined })
                      : setNewValue({ ...newValue, daily_weight_gain: parseFloat(e.target.value) || undefined })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    placeholder="pl. 1.1"
                  />
                  <p className="mt-1 text-xs text-gray-500">Az átlagos napi súlygyarapodás kilogrammban (0-10 kg között)</p>
                </div>
              )}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingValue ? editingValue.is_active : newValue.is_active}
                  onChange={(e) => editingValue
                    ? setEditingValue({ ...editingValue, is_active: e.target.checked })
                    : setNewValue({ ...newValue, is_active: e.target.checked })
                  }
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">{t('is_active', 'admin')}</label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowValueForm(false);
                    setEditingValue(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmAction !== null}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        title={confirmAction?.action === 'deleteCategory' ? 'Kategória törlése' : 'Érték törlése'}
        message={confirmAction?.action === 'deleteCategory'
          ? 'Biztosan törölni szeretnéd ezt a kategóriát?'
          : 'Biztosan törölni szeretnéd ezt az értéket?'
        }
        confirmLabel="Törlés"
      />
    </div>
  );
};

export default MasterDataManagement;